import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  LinearProgress,
  Chip,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
  Paper
} from '@mui/material';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const CompleteReviewList = () => {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [onlyUnreviewed, setOnlyUnreviewed] = useState(true);

  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/review/complete/documents/list', {
        params: { only_unreviewed: onlyUnreviewed }
      });
      setDocuments(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching documents:', err);
      setError('Failed to load documents. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [onlyUnreviewed]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleDocumentClick = (documentId) => {
    navigate(`/complete-review/${documentId}`);
  };

  const renderProgressBar = (label, value, total, color = 'primary') => {
    const percentage = total > 0 ? (value / total * 100) : 0;
    return (
      <Box sx={{ mb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption" color="textSecondary">
            {label}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {value} / {total}
          </Typography>
        </Box>
        <LinearProgress 
          variant="determinate" 
          value={percentage} 
          color={color}
          sx={{ height: 6, borderRadius: 3 }}
        />
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">
          <AssignmentTurnedInIcon sx={{ mr: 1, verticalAlign: 'bottom' }} />
          Complete Review
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={onlyUnreviewed}
              onChange={(e) => setOnlyUnreviewed(e.target.checked)}
            />
          }
          label="Show only unreviewed"
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {documents.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="textSecondary">
            {onlyUnreviewed 
              ? 'All documents have been fully reviewed!' 
              : 'No documents found.'}
          </Typography>
          {onlyUnreviewed && (
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              Toggle "Show only unreviewed" to see all documents.
            </Typography>
          )}
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {documents.map((doc) => (
            <Grid item xs={12} md={6} lg={4} key={doc.id}>
              <Card 
                sx={{ 
                  height: '100%', 
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                  }
                }}
                onClick={() => handleDocumentClick(doc.id)}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6" component="h2" noWrap>
                      {doc.name}
                    </Typography>
                    {doc.is_fully_reviewed && (
                      <Chip 
                        icon={<CheckCircleIcon />}
                        label="Complete" 
                        color="success" 
                        size="small" 
                      />
                    )}
                    {!doc.is_fully_reviewed && doc.questions_needing_review > 0 && (
                      <Chip 
                        label={`${doc.questions_needing_review} to review`} 
                        color="primary" 
                        size="small" 
                        variant="outlined"
                      />
                    )}
                  </Box>

                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    {doc.total_questions} questions • 
                    {doc.fully_processed_count > 0 && (
                      <>
                        {doc.fully_processed_count} processed • 
                      </>
                    )}
                    {doc.questions_needing_review > 0 && (
                      <>
                        <strong>{doc.questions_needing_review}</strong> need review • 
                      </>
                    )}
                    {new Date(doc.created_at).toLocaleDateString()}
                  </Typography>

                  <Box sx={{ mt: 2 }}>
                    {renderProgressBar(
                      'Topics', 
                      doc.review_progress.topics.verified,
                      doc.review_progress.topics.total,
                      'primary'
                    )}
                    {renderProgressBar(
                      'Answers', 
                      doc.review_progress.answers.verified,
                      doc.review_progress.answers.total,
                      'secondary'
                    )}
                    {renderProgressBar(
                      'Templates', 
                      doc.review_progress.templates.verified,
                      doc.review_progress.templates.total,
                      'success'
                    )}
                  </Box>

                  <Button 
                    fullWidth 
                    variant="contained" 
                    sx={{ mt: 2 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDocumentClick(doc.id);
                    }}
                  >
                    {doc.is_fully_reviewed ? 'View Review' : 'Start Review'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default CompleteReviewList;